// web-front/src/forecast-app/shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown.tsx
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import {ComboBox, Label, FlexColumn} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import environment from '../../../../../RelayEnvironment';
import {useInfiniteScroll} from '../../../hooks/useInfiniteScroll';

const ClientPrimaryContactDropdownQuery = graphql`
	query ClientPrimaryContactDropdownQuery($clientId: String, $first: Int, $after: String) {
		viewer {
			company {
				client(id: $clientId) {
					clientContacts(first: $first, after: $after) {
						pageInfo {
							endCursor
							hasNextPage
						}
						edges {
							node {
								id
								firstName
								lastName
							}
						}
					}
				}
			}
		}
	}
`;

const PAGE_SIZE = 20;

interface Contact {
	id: string;
	name: string;
}

interface ClientContactNode {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

interface ClientContactsQueryResponse {
	viewer?: {
		company?: {
			client?: {
				clientContacts?: {
					pageInfo: {endCursor: string | null; hasNextPage: boolean};
					edges: Array<{node: ClientContactNode}>;
				};
			};
		};
	};
}

interface ClientPrimaryContactDropdownProps {
	clientId: string;
	value: Contact | null;
	onChange: (contact: Contact | null) => void;
	label?: string;
}

export const ClientPrimaryContactDropdown = ({clientId, value, onChange, label}: ClientPrimaryContactDropdownProps) => {
	const {formatMessage} = useIntl();
	const [contacts, setContacts] = useState<Contact[]>(value ? [value] : []);
	const [cursor, setCursor] = useState<string | null>(null);
	const [hasNextPage, setHasNextPage] = useState(true);
	const loadingRef = useRef(false);

	const fetchPage = useCallback(
		(after: string | null) => {
			if (loadingRef.current) return;
			loadingRef.current = true;
			fetchQuery(environment.getInstance(), ClientPrimaryContactDropdownQuery, {
				clientId,
				first: PAGE_SIZE,
				after,
			})
				.toPromise()
				.then(
					(data: ClientContactsQueryResponse | undefined) => {
						const conn = data?.viewer?.company?.client?.clientContacts;
						if (!conn) {
							loadingRef.current = false;
							return;
						}
						const nodes: Contact[] = conn.edges.map(e => ({
							id: e.node.id,
							name: [e.node.firstName, e.node.lastName].filter(Boolean).join(' '),
						}));
						setContacts(prev => {
							if (after) return [...prev, ...nodes];
							// Preserve any pre-loaded contacts (e.g. the currently-selected primary
							// contact) that aren't returned in the first page of results.
							const nodeIds = new Set(nodes.map(n => n.id));
							const preserved = prev.filter(c => !nodeIds.has(c.id));
							return [...preserved, ...nodes];
						});
						setCursor(conn.pageInfo.endCursor);
						setHasNextPage(conn.pageInfo.hasNextPage);
						loadingRef.current = false;
					},
					() => {
						loadingRef.current = false;
					}
				);
		},
		[clientId]
	);

	useEffect(() => {
		fetchPage(null);
	}, [clientId]);

	const loadMore = useCallback(() => {
		if (!loadingRef.current && hasNextPage) {
			fetchPage(cursor);
		}
	}, [hasNextPage, cursor, fetchPage]);

	const {handleScroll} = useInfiniteScroll({hasNextPage, onLoadMore: loadMore});

	useEffect(() => {
		// The ComboBox renders its listbox inside a React Aria <Overlay> portal (appended to document.body),
		// so scroll events from the <ul role="listbox"> do not bubble to a wrapper div around <ComboBox>.
		// We use a MutationObserver to detect when the portal listbox appears/disappears and
		// attach a native scroll listener directly to it.
		let listboxEl: Element | null = null;

		const attachListener = (el: Element) => {
			listboxEl = el;
			el.addEventListener('scroll', handleScroll as unknown as EventListener);
		};

		const detachListener = () => {
			if (listboxEl) {
				listboxEl.removeEventListener('scroll', handleScroll as unknown as EventListener);
				listboxEl = null;
			}
		};

		const observer = new MutationObserver(() => {
			const found = document.querySelector('[role="listbox"]');
			if (found && found !== listboxEl) {
				detachListener();
				attachListener(found);
			} else if (!found && listboxEl) {
				detachListener();
			}
		});

		observer.observe(document.body, {childList: true, subtree: true});

		return () => {
			observer.disconnect();
			detachListener();
		};
	}, [handleScroll]);

	const dropdownLabel = label ?? formatMessage({id: 'clients.client-contact', defaultMessage: 'Primary Contact'});

	const handleChange = (key: string) => {
		if (!key) {
			onChange(null);
			return;
		}
		const found = contacts.find(c => c.id === key) ?? null;
		onChange(found);
	};

	return (
		<FlexColumn gap={'s'}>
			<Label>{dropdownLabel}</Label>
			<ComboBox defaultItems={contacts} value={value?.id ?? undefined} onChange={handleChange} width={'flex'}>
				{(contact: Contact) => (
					<ComboBox.Item key={contact.id} textValue={contact.name}>
						{contact.name}
					</ComboBox.Item>
				)}
			</ComboBox>
		</FlexColumn>
	);
};
